import { createContext, useEffect, useState } from "react";
import { MapMarkerNewConfirm } from "./Map-marker-new-confirm";
import { MapMarkerNewFeatureList } from "./Map-marker-new-feature-list";
import { AddProjection, MAP, MARKER_LAYER } from "../Map-marker";
import WKT from "ol/format/WKT";
import { Feature, getUid } from "ol";
import { BehaviorSubject, filter, take } from "rxjs";
import { createEmpty, extend } from "ol/extent";
import { MapMarkerAddButton } from "./Map-marker-add-button";
import { SimpleGeometry } from "ol/geom";
import { MINIMAP } from "./Map-marker-mini-map";

interface Item {
	street?: string;
	start?: string;
	end?: string;
	geom: string;
	address?: string;
	openlr: any;
	detour: boolean;
	twoWay?: boolean;
	turnedAround?: boolean;
}

interface Props {
	options: { readOnly?: boolean; sticky?: boolean; onlyOne?: boolean; tag?: string; detour?: boolean };
	onChange(items: Item[]): unknown;
	value?: Item[];
}

let propItems: Item[] | undefined;

export const mapSize$ = new BehaviorSubject<[number, number]>([0, 0]);

export interface ErrorMessage {
	error: "point_error" | "measure_error" | "general_error";
	message: string;
	uid: string;
}

const defaultContextValues: {
	errors: ErrorMessage[];
	selectedItem?: Feature;
	readOnly: boolean;
	sticky: boolean;
	tag?: string;
	detour: boolean;
	isLoading: boolean;
	onDelete: (items: Feature[]) => void;
	onSelect: (item?: Feature) => void;
	onError: (error: ErrorMessage) => void;
	removeError: (feature: Feature) => void;
	onLoading: (value: boolean) => void;
} = {
	isLoading: false,
	readOnly: false,
	sticky: true,
	tag: undefined,
	detour: false,
	onDelete: () => {},
	onSelect: () => {},
	onError: () => {},
	errors: [],
	removeError: () => {},
	onLoading: () => {},
};

export const findError = (feature?: Feature, errors?: ErrorMessage[]) => {
	if (!feature || !errors) return undefined;

	const uid = getUid(feature);
	return errors.find((e) => e.uid === uid);
};
export const NewFeatureListContext = createContext(defaultContextValues);

export const MapMarkerListWrapper = (props: Props) => {
	const [items, setItems] = useState<Feature[]>([]);
	const [context, setContext] = useState(defaultContextValues);

	const handleDelete = (features: Feature[]) => {
		MARKER_LAYER.getSource()?.removeFeatures(features);
		const uids = features.map((f) => getUid(f));
		setItems(items.filter((i) => !uids.includes(getUid(i))));
	};

	const handleError = (error: ErrorMessage) => {
		setContext((prevContext) => ({ ...prevContext, errors: [...prevContext.errors, error] }));
	};

	const handleErrorRemoval = (feature: Feature) => {
		setContext((prevContext) => ({ ...prevContext, errors: prevContext.errors.filter((e) => e.uid !== getUid(feature)) }));
		feature.set("error", false, true);
	};

	const selectItem = (item?: Feature) => {
		if (item && !item.getGeometry()?.intersectsCoordinate([0, 0]))
			MINIMAP.getView()?.fit(item.getGeometry() as SimpleGeometry, { padding: [120, 120, 120, 120], maxZoom: 12, duration: 500 });
		setContext((prevContext) => ({ ...prevContext, selectedItem: item }));
	};

	const handleSetLoading = (value: boolean) => {
		setContext((prevContext) => ({ ...prevContext, isLoading: value }));
	};

	useEffect(() => {
		setContext((v) => ({
			readOnly: props.options.readOnly ?? false,
			sticky: props.options.sticky ?? true,
			tag: props.options.tag,
			detour: !!props.options.detour,
			onDelete: handleDelete,
			onSelect: selectItem,
			onError: handleError,
			removeError: handleErrorRemoval,
			selectedItem: v.selectedItem,
			errors: v.errors,
			isLoading: v.isLoading,
			onLoading: handleSetLoading,
		}));
	}, [props.options.readOnly, props.options.sticky, props.options.tag, props.options.detour, items]);

	useEffect(() => {
		const source = MARKER_LAYER.getSource()!;
		source.on("change", () => {
			const features = source.getFeatures().filter((f) => !!f.get("detour") === !!props.options.detour);
			setItems(features);
			const data: Item[] = features.map((f) => {
				return {
					street: f.get("street"),
					start: f.get("start")?.toString(),
					end: f.get("end")?.toString(),
					geom: new WKT().writeGeometry(f.getGeometry()!, {
						dataProjection: "EPSG:3059",
						featureProjection: "EPSG:3857",
					}),
					address: f.get("address"),
					openlr: {
						binary: f.get("openlr")?.[0]?.["binary"],
						latitude: f.get("openlr")?.[0]?.["latitude"],
						longitude: f.get("openlr")?.[0]?.["longitude"],
						locationreference_xml: f.get("openlr")?.[0]?.["pointlocationreference_xml"] ?? f.get("openlr")?.[0]?.["linelocationreference_xml"],
					},
					error: !!f.get("error"),
					detour: !!f.get("detour"),
					twoWay: !!f.get("twoWay"),
					turnedAround: !!f.get("turnedAround"),
				};
			});

			props.onChange?.(data);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (propItems && JSON.stringify(propItems) === JSON.stringify(props.value)) return;

		const source = MARKER_LAYER.getSource()!;

		propItems = props.value;
		console.log(" props.value:", props.value);

		if (propItems && propItems.length > 0) {
			source.removeFeatures(source.getFeatures().filter((f) => f.get("detour") === !!props.options.detour));

			AddProjection();
			const features = propItems
				?.map((i: Item) => {
					const feature = new WKT().readFeature(i.geom, {
						dataProjection: "EPSG:3059",
						featureProjection: "EPSG:3857",
					});
					feature.setProperties({
						street: i.street,
						start: i.start,
						end: i.end,
						address: i.address,
						detour: !!props.options.detour,
						twoWay: !!i.twoWay,
						turnedAround: !!i.turnedAround,
						openlr: [
							{
								binary: i.openlr?.binary,
								latitude: i.openlr?.latitude,
								longitude: i.openlr?.longitude,
								pointlocationreference_xml: i.openlr?.locationreference_xml,
							},
						],
						DONT_UPDATE: true,
					});
					return feature;
				})
				.filter((f: Feature) => {
					const coords = ((f.getGeometry() as any).flatCoordinates as number[]).toString();
					return !source.getFeatures().find((cf) => ((cf.getGeometry() as any).flatCoordinates as number[]).toString() === coords);
				});
			if (features.length > 0) {
				mapSize$
					.pipe(
						filter((size) => size[0] !== 0),
						take(1)
					)
					.subscribe((size) => {
						const extent = createEmpty();
						features.forEach((feature: Feature) => extend(extent, feature.getGeometry()!.getExtent()));
						MAP.getView().fit(extent, { maxZoom: 10, size: size });
					});
			}
			source.addFeatures(features);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.value]);

	return (
		<NewFeatureListContext.Provider value={context}>
			<div className="flex flex-col gap-5">
				{!props.options.detour && <MapMarkerNewConfirm />}
				<MapMarkerNewFeatureList items={items} />
				<MapMarkerAddButton />
			</div>
		</NewFeatureListContext.Provider>
	);
};
